import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown'
import { type Metric } from 'src/home/reportTemplateParameters'
import {
  type HomePageFilterParameter,
  type HomePageTableDimension,
  type ProgressOverTimeChartAggregation,
  type TimePeriodType
} from 'src/home/components/utils'
import { formatSelectedFiltersForToolDescription } from 'src/home/copilot/toolLogic'
import { type ActiveFilter } from 'shared/components/Filter'
import type { CopilotPageContext, CopilotSuggestedPrompt } from 'shared/copilot/pageContext/types'
import { type SummaryData } from 'src/home/components/Summary'
import { type ProgressOverTimeRecords } from 'src/home/components/ProgressOvertime'
import { METRICS_WITH_REVERSED_COLOR_SCHEME } from 'src/home/constants'
import { getDifferenceInDays } from 'shared/dateFns'
import { getFormattedValue } from 'shared/numberFormat'
import { getPercentageDisplayValueAndStatus } from 'shared/utils'
import {
  type Report,
  type TransformToDetailedDataFn,
  type TransformToTimeSeriesByMetricAndBreakdownFn
} from 'src/home/transformations'
import { getScorecardMetricElementId } from 'src/home/copilot/scorecardElements'

interface HomeCopilotMetric {
  id: Metric
  label: string
}

export interface HomeCopilotScorecardSparklinePoint {
  dateLabel: string
  value: number
}

export interface HomeCopilotScorecardMetricDetails {
  id: Metric
  label: string
  summaryValue: number
  summaryValueFormatted: string
  previousValue: number
  comparisonDisplayValue: string
  comparisonStatus: 'positive' | 'negative' | 'equal'
  reverseColorScheme: boolean
  sparklineDataPoints: HomeCopilotScorecardSparklinePoint[]
}

interface HomeCopilotScorecardDataInput {
  selectedTopCardMetrics: HomeCopilotMetric[]
  summaryData: SummaryData | null
  summaryDataForPreviousTimePeriod: SummaryData | null
  progressOverTimeRecords: Partial<Record<Metric, ProgressOverTimeRecords>>
  currencyCode: string
}

interface HomeTimeSeriesChartState {
  selectedMetrics: string
  selectedBreakdowns: string
  aggregation: string
  comparisonMode: string
}

interface HomeDetailedTableColumn {
  id: string
  label: string
}

interface HomeDetailedTableState {
  selectedDimensions: HomeDetailedTableColumn[]
  selectedMetrics: HomeCopilotMetric[]
}

export const COPILOT_HOME_DETAILED_TABLE_MAX_INSPECT_ROWS = 50
export const COPILOT_HOME_PROGRESS_OVER_TIME_MAX_INSPECT_POINTS = 90

export interface HomeCopilotDetailedTableInspectRow {
  dimensions: Record<string, string>
  metrics: Record<string, { value: number | null; formatted: string }>
}

export interface HomeCopilotDetailedTableInspectData {
  rowCount: number
  displayedRowCount: number
  truncated: boolean
  rows: HomeCopilotDetailedTableInspectRow[]
}

export interface HomeCopilotProgressOverTimeInspectData {
  dataPointCount: number
  displayedDataPointCount: number
  truncated: boolean
  dataPoints: Array<{
    dateLabel: string
    fullDate: string
    values: Record<string, number | string>
  }>
}

interface HomeRecommendationState {
  status: string
  totalCount: number
  visibleCardCount: number
  severityCounts: Record<string, number>
  statusCounts: Record<string, number>
  topRecommendations: Array<{
    name: string
    severity: string
    status: string
    affectedEntities: number
    totalEntities: number
  }>
}

/**
 * Type guard for table cell values that can be formatted as numeric metrics.
 * Excludes null and NaN so inspect payloads only expose real numbers.
 */
const isNumericRowValue = (value: unknown): value is number => {
  return typeof value === 'number' && !Number.isNaN(value)
}

/**
 * Builds a truncated inspect payload from detailed table rows for `inspect_current_page`.
 * Caps row count and formats dimension and metric values for agent consumption.
 */
export const buildHomeCopilotDetailedTableInspectData = ({
  reports,
  reportsForPreviousTimePeriod,
  selectedDimensions,
  selectedMetrics,
  transformToDetailedReportData,
  currencyCode,
  getDimensionLabel,
  getMetricLabel,
  maxRows = COPILOT_HOME_DETAILED_TABLE_MAX_INSPECT_ROWS
}: {
  reports: Report[]
  reportsForPreviousTimePeriod: Report[]
  selectedDimensions: HomePageTableDimension[]
  selectedMetrics: Metric[]
  transformToDetailedReportData: TransformToDetailedDataFn<Report> | undefined
  currencyCode: string
  getDimensionLabel: (dimensionId: HomePageTableDimension) => string
  getMetricLabel: (metricId: Metric) => string
  maxRows?: number
}): HomeCopilotDetailedTableInspectData => {
  if (reports.length === 0 || transformToDetailedReportData == null) {
    return {
      rowCount: 0,
      displayedRowCount: 0,
      truncated: false,
      rows: []
    }
  }

  const dimensions = selectedDimensions
  const metrics = selectedMetrics
  const tableRows = transformToDetailedReportData({
    reports,
    reportsForPreviousTimePeriod,
    selectedDimensions: dimensions
  })

  const formatInspectDimensionValue = (value: unknown): string => {
    if (value == null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)

    return JSON.stringify(value)
  }

  const inspectRows = tableRows.slice(0, maxRows).map((row) => {
    return {
      dimensions: Object.fromEntries(
        dimensions.map((dimension) => [getDimensionLabel(dimension), formatInspectDimensionValue(row[dimension])])
      ),
      metrics: Object.fromEntries(
        metrics.map((metric) => {
          const value = row[metric]
          const numericValue = isNumericRowValue(value) ? value : null

          return [
            getMetricLabel(metric),
            {
              value: numericValue,
              formatted:
                numericValue == null
                  ? '—'
                  : getFormattedValue({
                      value: numericValue,
                      id: metric,
                      currencyCode
                    })
            }
          ]
        })
      )
    }
  })

  return {
    rowCount: tableRows.length,
    displayedRowCount: inspectRows.length,
    truncated: tableRows.length > maxRows,
    rows: inspectRows
  }
}

/**
 * Builds a truncated time-series inspect payload from chart data for `inspect_current_page`.
 * Caps data points and strips non-numeric series values from each point.
 */
export const buildHomeCopilotProgressOverTimeChartInspectData = ({
  reports,
  reportsForPreviousTimePeriod,
  chartSettings,
  dateRange,
  selectedPeriodType,
  isBudgetMode,
  transformToTimeSeriesByMetricAndBreakdown,
  maxPoints = COPILOT_HOME_PROGRESS_OVER_TIME_MAX_INSPECT_POINTS
}: {
  reports: Report[]
  reportsForPreviousTimePeriod: Report[]
  chartSettings: {
    metrics: Metric[]
    marketIds: string[]
    channelIds: string[]
    aggregation: ProgressOverTimeChartAggregation
    comparisonMode: boolean
  }
  dateRange: {
    startDate: string
    endDate: string
  }
  selectedPeriodType: TimePeriodType
  isBudgetMode: boolean
  transformToTimeSeriesByMetricAndBreakdown: TransformToTimeSeriesByMetricAndBreakdownFn<Report, Metric> | undefined
  maxPoints?: number
}): HomeCopilotProgressOverTimeInspectData => {
  if (reports.length === 0 || transformToTimeSeriesByMetricAndBreakdown == null) {
    return {
      dataPointCount: 0,
      displayedDataPointCount: 0,
      truncated: false,
      dataPoints: []
    }
  }

  const { metrics, marketIds, channelIds, aggregation, comparisonMode } = chartSettings
  const selectedBreakdownCount = marketIds.length + channelIds.length

  const chartData = transformToTimeSeriesByMetricAndBreakdown({
    records: reports,
    previousRecords: reportsForPreviousTimePeriod,
    metrics,
    channels: channelIds,
    markets: marketIds,
    daysRange: getDifferenceInDays(dateRange.startDate, dateRange.endDate),
    isComparisonMode: selectedBreakdownCount <= 2 && comparisonMode,
    selectedPeriodType,
    isBudgetMode,
    isCumulativeMode: aggregation === 'cumulative'
  })

  const dataPoints = chartData.slice(0, maxPoints).map((record) => {
    const { x, fullDate, ...seriesValues } = record

    return {
      dateLabel: x,
      fullDate,
      values: Object.fromEntries(
        Object.entries(seriesValues).filter(([, value]) => typeof value === 'number')
      ) as Record<string, number | string>
    }
  })

  return {
    dataPointCount: chartData.length,
    displayedDataPointCount: dataPoints.length,
    truncated: chartData.length > maxPoints,
    dataPoints
  }
}

/**
 * Builds per-metric scorecard details for page context and element inspection.
 * Includes formatted values, period comparison, and sparkline points shown on each card.
 */
export const buildHomeCopilotScorecardMetrics = ({
  selectedTopCardMetrics,
  summaryData,
  summaryDataForPreviousTimePeriod,
  progressOverTimeRecords,
  currencyCode
}: HomeCopilotScorecardDataInput): HomeCopilotScorecardMetricDetails[] => {
  if (summaryData == null || summaryDataForPreviousTimePeriod == null) return []

  return selectedTopCardMetrics.map(({ id, label }) => {
    const summaryValue = summaryData[id as keyof SummaryData] ?? 0
    const previousValue = summaryDataForPreviousTimePeriod[id as keyof SummaryData] ?? 0
    const { displayValue: comparisonDisplayValue, percentageStatus: comparisonStatus } =
      getPercentageDisplayValueAndStatus(summaryValue, previousValue)
    const progressOverTime = progressOverTimeRecords[id] ?? []

    return {
      id,
      label,
      summaryValue,
      summaryValueFormatted: getFormattedValue({
        value: summaryValue,
        currencyCode,
        id,
        compact: true
      }),
      previousValue,
      comparisonDisplayValue,
      comparisonStatus,
      reverseColorScheme: METRICS_WITH_REVERSED_COLOR_SCHEME.includes(id),
      sparklineDataPoints: progressOverTime.map((record) => ({
        dateLabel: record.x,
        value: record[id] as number
      }))
    }
  })
}

export interface BuildHomeCopilotPageContextArgs {
  reportName?: string
  reportTemplate?: string
  dateRange: {
    startDate: string
    endDate: string
  }
  previousTimePeriod: [string, string]
  selectedFilters: Array<ActiveFilter<HomePageFilterParameter>>
  selectedTopCardMetrics: HomeCopilotMetric[]
  scorecardMetrics: HomeCopilotScorecardMetricDetails[]
  detailedTableState: HomeDetailedTableState
  detailedTableInspectData: HomeCopilotDetailedTableInspectData
  progressOverTimeChartInspectData: HomeCopilotProgressOverTimeInspectData
  reportRowCount: number
  timeSeriesChartState: HomeTimeSeriesChartState
  recommendationState: HomeRecommendationState
  selectedConversionEvents?: MultiSelectDropdownItem[]
  isBudgetMode: boolean
  canFilter: boolean
  dataStatus: string
  isDataLoaded: boolean
  suggestedPrompts?: CopilotSuggestedPrompt[]
}

/**
 * Formats a conversion event label for page context summaries.
 * Appends the event source in parentheses when a subCategory is present.
 */
const formatConversionEventLabel = (event: MultiSelectDropdownItem): string => {
  if (event.subCategory == null) return event.label

  return `${event.label} (${event.subCategory})`
}

/**
 * Formats the selected conversion events for human-readable page context summaries.
 * Returns `none` when no conversion events are selected.
 */
const formatConversionEvents = (selectedConversionEvents: MultiSelectDropdownItem[] | undefined): string => {
  if (selectedConversionEvents == null || selectedConversionEvents.length === 0) return 'none'

  return selectedConversionEvents.map(formatConversionEventLabel).join(', ')
}

/**
 * Assembles the full home-dashboard `CopilotPageContext` sent to the agent and UI.
 * Describes page sections, inspect payloads, related tools, and current dashboard state.
 */
export const buildHomeCopilotPageContext = ({
  reportName,
  reportTemplate,
  dateRange,
  previousTimePeriod,
  selectedFilters,
  selectedTopCardMetrics,
  scorecardMetrics,
  detailedTableState,
  detailedTableInspectData,
  progressOverTimeChartInspectData,
  reportRowCount,
  timeSeriesChartState,
  recommendationState,
  selectedConversionEvents,
  isBudgetMode,
  canFilter,
  dataStatus,
  isDataLoaded,
  suggestedPrompts
}: BuildHomeCopilotPageContextArgs): CopilotPageContext => {
  const selectedMetricLabels = selectedTopCardMetrics.map((metric) => metric.label).join(', ')
  const selectedMetricsDescription = selectedMetricLabels.length > 0 ? selectedMetricLabels : 'none'
  const activeFilters = formatSelectedFiltersForToolDescription(selectedFilters)
  const conversionEvents = formatConversionEvents(selectedConversionEvents)
  const reportDescription = ((): string => {
    if (reportName == null) return 'No report is currently selected'
    if (reportTemplate == null) return reportName

    return `${reportName} (${reportTemplate})`
  })()

  return {
    pageId: 'home',
    label: 'Home dashboard',
    description: `
      The Alvie home dashboard with performance scorecards, progress over time, date and filter controls, and recommendations.
      This page is the first page the user sees when they navigate to the app and contains high level performance (channel/market level) metrics for the client.
      The page is divided into two sections:
      1. The left section (and the bottom of the page) - Performance Data
      This section displays performance data related to the the workspace the user is currently in. It includes data broken down by channels, markets, and dates.
      This data is based on the user's configured 'Starter Report' Reporting Solution. If it's not configured the homepage will display a prompt to configure it.
      2. The right section - Recommendations
      This section displays a list of recommendations based on the Recommendation feature. These recommendations are generated based on a long list of expert-knowledge based rules from Precis (the agency behind Alvie).
      The recommendations are displayed in a list of cards, each with a title, a description, their severity as well as a clickhandler that will open a detailed popup of the invidual recommendation.

      Important context for this page is that anywhere a number is related to conversions, it's always based on the selected conversion event: ${conversionEvents}.
      This means if the user asks for revenue/conversions related metrics, you need to take this into consideration and potentially use the tool available to change it to fit what the user asked for.
      `,
    summary: `The user is viewing the home dashboard. Report: ${reportDescription}. Date range: ${dateRange.startDate} to ${dateRange.endDate}, compared with ${previousTimePeriod[0]} to ${previousTimePeriod[1]}. Active filters: ${activeFilters}. Visible scorecard metrics: ${selectedMetricsDescription}. Selected conversion event: ${conversionEvents}. Data status: ${dataStatus}.`,
    state: {
      reportName,
      reportTemplate,
      dateRange,
      previousTimePeriod,
      activeFilters,
      selectedTopCardMetrics,
      selectedConversionEvents:
        selectedConversionEvents?.map((event) => ({
          value: event.value,
          label: event.label,
          source: event.subCategory
        })) ?? [],
      isBudgetMode,
      canFilter,
      dataStatus,
      isDataLoaded
    },
    elements: [
      {
        id: 'date_range',
        label: 'Date range',
        description: 'The selected reporting date range and comparison period.',
        details: {
          current: dateRange,
          comparison: previousTimePeriod
        },
        relatedTools: ['update_date_range']
      },
      {
        id: 'filters',
        label: 'Filters',
        description:
          'The active homepage filters and whether filtering is currently available. The filters are applied to all charts on the page.',
        details: {
          canFilter,
          activeFilters
        },
        relatedTools: ['list_filter_options', 'set_page_filters']
      },
      {
        id: 'scorecard_charts',
        label: 'Scorecard group',
        description:
          'The row of homepage scorecard metric cards. Use elementId "scorecard_charts" to highlight the whole group, or elementId "scorecard_chart_{metricId}" (for example scorecard_chart_roas) to highlight one scorecard.',
        details: {
          selectedMetrics: selectedMetricLabels,
          dateRange: `${dateRange.startDate} to ${dateRange.endDate}`,
          metrics: scorecardMetrics.map(({ label, summaryValueFormatted, comparisonDisplayValue }) => ({
            label,
            summaryValue: summaryValueFormatted,
            comparison: comparisonDisplayValue
          }))
        },
        inspectDetails: {
          selectedTopCardMetrics,
          dateRange,
          comparisonPeriod: previousTimePeriod,
          metrics: scorecardMetrics,
          description:
            'Each visible scorecard has a compact trend chart for its metric over the selected date range. summaryValueFormatted and comparisonDisplayValue match what the user sees on the card. sparklineDataPoints are ordered left-to-right as shown in the sparkline.'
        },
        relatedTools: [
          'list_available_metrics_for_scorecard_group',
          'select_metrics_in_scorecard_group',
          'highlight_element',
          'inspect_current_page'
        ]
      },
      ...scorecardMetrics.map((metric) => ({
        id: getScorecardMetricElementId(metric.id),
        label: `${metric.label} scorecard`,
        description: `The homepage scorecard for ${metric.label}, including its sparkline and period comparison.`,
        details: {
          metricId: metric.id,
          summaryValue: metric.summaryValueFormatted,
          comparison: metric.comparisonDisplayValue
        },
        inspectDetails: metric,
        relatedTools: ['highlight_element', 'inspect_current_page']
      })),
      {
        id: 'progress_over_time_chart',
        label: 'Progress over time chart',
        description:
          'The time-series chart below the scorecards. Best for showing how metrics change over time, optionally split by market or channel. Use inspect_current_page with elementId "progress_over_time_chart" to read the rendered chart values.',
        details: {
          selectedMetrics: timeSeriesChartState.selectedMetrics,
          selectedBreakdowns: timeSeriesChartState.selectedBreakdowns,
          aggregation: timeSeriesChartState.aggregation,
          dataPointCount: progressOverTimeChartInspectData.dataPointCount
        },
        inspectDetails: {
          dateRange,
          comparisonPeriod: previousTimePeriod,
          selectedMetrics: timeSeriesChartState.selectedMetrics,
          selectedBreakdowns: timeSeriesChartState.selectedBreakdowns,
          aggregation: timeSeriesChartState.aggregation,
          comparisonMode: timeSeriesChartState.comparisonMode,
          isBudgetMode,
          chartData: progressOverTimeChartInspectData,
          description:
            'Use this chart for time-series analysis, such as daily spend trends or conversion value over time. Prefer the detailed data table when the user only needs summarized totals by market, channel, or date. chartData contains the visible time-series points currently rendered in the chart; values keys match the chart legend series names.'
        },
        relatedTools: [
          'list_available_progress_over_time_chart_settings',
          'update_progress_over_time_chart_settings',
          'inspect_current_page'
        ]
      },
      {
        id: 'detailed_table',
        label: 'Detailed data table',
        description:
          'The detailed report table below the chart. Best for summarized breakdowns across dimensions such as market, channel, or date. Use inspect_current_page with elementId "detailed_table" to read the rendered table rows and values.',
        details: {
          rowCount: detailedTableInspectData.rowCount,
          dimensions: detailedTableState.selectedDimensions.map((dimension) => dimension.label).join(', '),
          metrics: detailedTableState.selectedMetrics.map((metric) => metric.label).join(', ')
        },
        inspectDetails: {
          rowCount: detailedTableInspectData.rowCount,
          dateRange,
          comparisonPeriod: previousTimePeriod,
          selectedDimensions: detailedTableState.selectedDimensions,
          selectedMetrics: detailedTableState.selectedMetrics,
          activeFilters,
          reportDescription,
          tableData: detailedTableInspectData,
          description:
            'Use this table for summarized breakdowns in the selected date range, such as total spend per market or conversion value per channel. Prefer the progress over time chart when the user wants to see how metrics evolve over time. tableData.rows contains the aggregated rows currently shown in the table with formatted metric values.'
        },
        relatedTools: [
          'list_available_detailed_table_settings',
          'update_detailed_table_settings',
          'inspect_current_page'
        ]
      },
      {
        id: 'recommendations',
        label: 'Recommendations',
        description: 'The recommendation cards shown on the right side of the home dashboard.',
        details: {
          status: recommendationState.status,
          visibleCardCount: recommendationState.visibleCardCount,
          totalCount: recommendationState.totalCount
        },
        inspectDetails: {
          ...recommendationState,
          description:
            'The right-side recommendations panel summarizes optimization, tracking, data quality, and best-practice issues. Cards can be opened for details, assignment, status changes, and related affected entities.'
        },
        relatedTools: ['change_url']
      }
    ],
    suggestedPrompts
  }
}
